import axios from 'axios';
import * as Constants from './Constants';
import { clearSidebarIconOnlyPreference } from './sidebarMode';

export const CS_LOGOUT_URL = 'https://cs.hngpl.org/login/logout.php';
export const CS_ORIGIN = 'https://cs.hngpl.org';
export const LOGIN_PATH = '/user-pages/login-1';
export const LOGIN_HASH = '#/user-pages/login-1';
export const PHP_SESSION_EXPIRED_MESSAGE_TYPE = 'php-session-expired';
export const PHP_SESSION_EXPIRED_MESSAGE_SOURCE = 'hngpl-cs';

/** Late-bound stop for billing idle watch — avoids circular import with billingSessionTimeout.js */
let stopBillingSessionWatchFn = null;

export function registerBillingSessionStop(fn) {
  stopBillingSessionWatchFn = typeof fn === 'function' ? fn : null;
}

function clearBrowserCookies() {
  try {
    const hostname = window.location.hostname || '';
    const domains = ['', hostname];
    if (hostname.indexOf('.') >= 0) {
      domains.push('.' + hostname.replace(/^www\./, ''));
    }

    const raw = document.cookie || '';
    const names = raw.split(';').map((part) => part.split('=')[0].trim()).filter(Boolean);

    names.forEach((name) => {
      domains.forEach((domain) => {
        const domainAttr = domain ? ';domain=' + domain : '';
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/' + domainAttr;
      });
    });
  } catch (e) {
    // Best-effort cookie cleanup on billing origin.
  }
}

function clearCsSessionViaIframe(userId) {
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src = CS_LOGOUT_URL + (userId ? '?user_id=' + encodeURIComponent(userId) : '');
    document.body.appendChild(iframe);
    window.setTimeout(function () {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    }, 5000);
  } catch (e) {
    // Best-effort; Java revoke still runs.
  }
}

/**
 * Revoke Java billing_login_session. Uses sendBeacon/fetch keepalive so the
 * request survives SPA navigation / hard redirect on idle timeout.
 */
function revokeJavaSession(userId) {
  if (!userId) {
    return;
  }
  const url = Constants.BACKEND_URL + '/user/logout?userId=' + encodeURIComponent(userId);
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url);
    }
  } catch (e) {
    // ignore
  }
  try {
    if (typeof fetch === 'function') {
      fetch(url, { method: 'POST', keepalive: true, mode: 'cors', credentials: 'omit' });
    }
  } catch (e) {
    // ignore
  }
  try {
    axios.post(url).catch(function () {});
  } catch (e) {
    // ignore
  }
}

function clearLocalAuthState() {
  if (stopBillingSessionWatchFn) {
    try {
      stopBillingSessionWatchFn();
    } catch (e) {
      // ignore
    }
  }
  localStorage.removeItem('Username');
  localStorage.removeItem('UserType');
  localStorage.removeItem('UserId');
  localStorage.removeItem('routes');
  clearSidebarIconOnlyPreference();
  try {
    sessionStorage.removeItem('billingLastActivity');
    sessionStorage.clear();
  } catch (e) {
    // ignore
  }
  clearBrowserCookies();
}

function hardRedirectToLogin() {
  // Unique query forces a full document load; hash-only changes often do not.
  const target = window.location.origin + '/?logged_out=' + Date.now() + LOGIN_HASH;
  try {
    window.location.replace(target);
  } catch (e) {
    try {
      window.location.href = target;
    } catch (e2) {
      window.location.hash = LOGIN_HASH;
      window.location.reload();
    }
  }
}

/**
 * Revoke Java + PHP sessions and clear local auth state.
 * Does not navigate — use when already on (or about to show) the login page.
 * Returns true if there was a session to clear.
 */
export function clearAllSessions(options) {
  const userId = localStorage.getItem('UserId');
  const reason = options && options.reason;
  const fromPhpExpiry = reason === 'php-session-expired';
  const hadSession = !!(userId || localStorage.getItem('Username') || localStorage.getItem('routes'));

  // 1) Java API sign-out (keepalive so it survives unload)
  revokeJavaSession(userId);

  // 2) PHP session destroy via hidden iframe (skip if PHP already reported expiry)
  if (!fromPhpExpiry) {
    clearCsSessionViaIframe(userId);
  }

  // 3) Destroy React / browser auth state
  clearLocalAuthState();

  return hadSession;
}

/**
 * Sign out of billing CRM, revoke billing_login_session (Java), and clear CS PHP session (iframe).
 * Always hard-redirects to the React login page so the authenticated shell cannot linger.
 */
export function performLogout(history, options) {
  clearAllSessions(options);

  // Leave authenticated UI immediately (SPA + hard reload fallback)
  try {
    if (history && typeof history.replace === 'function') {
      history.replace(LOGIN_PATH);
    } else if (history && typeof history.push === 'function') {
      history.push(LOGIN_PATH);
    }
  } catch (e) {
    // ignore — hard redirect below is authoritative
  }

  hardRedirectToLogin();
}

/**
 * True when a window message is the CS PHP idle-expiry notice for billing CRM.
 */
export function isPhpSessionExpiredMessage(event) {
  if (!event || event.origin !== CS_ORIGIN) {
    return false;
  }
  const data = event.data;
  return !!(
    data
    && data.source === PHP_SESSION_EXPIRED_MESSAGE_SOURCE
    && data.type === PHP_SESSION_EXPIRED_MESSAGE_TYPE
  );
}
